import React, { useEffect, useRef } from "react";
import { Html5QrcodeScanner } from "html5-qrcode/esm/html5-qrcode-scanner";
import { Html5QrcodeScanType } from "html5-qrcode/esm/core";

interface QRScannerProps {
  onResult: (result: string) => void;
  onError?: (error: string) => void;
  isOpen?: boolean;
}

export const QRScanner: React.FC<QRScannerProps> = ({
  onResult,
  onError,
  isOpen = false,
}) => {
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const scannerId = useRef(`qr-reader-${Math.random().toString(36).substr(2, 9)}`).current;
  const isProcessing = useRef(false);

  const onScanSuccess = (decodedText: string) => {
    // Prevent multiple detections of the same QR code
    if (isProcessing.current) return;
    
    isProcessing.current = true;
    console.log("QR detected:", decodedText);
    onResult(decodedText);
    
    // Stop scanner immediately after successful scan
    if (scannerRef.current) {
      try {
        const element = document.getElementById(scannerId);
        if (element) {
          scannerRef.current.clear();
        }
      } catch (error) {
        console.warn("Error clearing scanner after success:", error);
      } finally {
        scannerRef.current = null;
        isProcessing.current = false;
      }
    }
  };

  // Initialize scanner
  useEffect(() => {
    // Clean up any existing scanner first
    if (scannerRef.current) {
      try {
        scannerRef.current.clear();
      } catch (error) {
        console.warn("Error clearing existing scanner:", error);
      }
      scannerRef.current = null;
    }

    if (!isOpen) {
      return;
    }

    // Wait for DOM element to be available
    const initScanner = () => {
      const element = document.getElementById(scannerId);
      if (element && !scannerRef.current) {
        console.log("Initializing scanner");
        isProcessing.current = false; // Reset processing flag
        scannerRef.current = new Html5QrcodeScanner(
          scannerId,
          { 
            fps: 10, 
            qrbox: { width: 350, height: 350 },
            supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
          },
          false
        );
        console.log("Scanner initialized");
        scannerRef.current.render(onScanSuccess, (error) => {
          // Only show meaningful errors, not "not found" messages
          if (!error.includes("NotFoundException") && !error.includes("No MultiFormat Readers")) {
            onError?.(error);
          }
        });
      }
    };

    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(initScanner, 100);

    return () => {
      clearTimeout(timeoutId);
      if (scannerRef.current) {
        try {
          const element = document.getElementById(scannerId);
          if (element) {
            scannerRef.current.clear();
          }
        } catch (error) {
          console.warn("Error clearing scanner:", error);
        }
        scannerRef.current = null;
      }
    };
  }, [isOpen]);

  return (
    <div className="w-full max-w-2xl mx-auto qr-scanner-container">
      <style>{`
        /* Responsive container sizing */
        @media (max-width: 640px) {
          .qr-scanner-container {
            max-width: 100% !important;
          }
          .qr-scanner-container > div {
            min-height: 350px !important;
          }
        }
        @media (min-width: 641px) and (max-width: 1024px) {
          .qr-scanner-container {
            max-width: 90% !important;
          }
          .qr-scanner-container > div {
            min-height: 400px !important;
          }
        }
        
        /* Button styling */
        .qr-scanner-container button {
          background: linear-gradient(to right, #EB1C37, #EB1C37) !important;
          color: white !important;
          border: none !important;
          border-radius: 8px !important;
          padding: 12px 24px !important;
          font-weight: 500 !important;
          font-size: 14px !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 2px 4px rgba(224, 69, 48, 0.3) !important;
        }
        .qr-scanner-container button:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 4px 8px rgba(224, 69, 48, 0.4) !important;
        }
        @media (max-width: 640px) {
          .qr-scanner-container button {
            padding: 10px 18px !important;
            font-size: 13px !important;
          }
        }
        
        /* Label and radio styling */
        .qr-scanner-container label {
          cursor: pointer !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
        }
        .qr-scanner-container input[type="radio"] {
          cursor: pointer !important;
        }
        
        /* Camera select dropdown - project standard design */
        .qr-scanner-container select {
          display: block !important;
          width: 100% !important;
          height: 40px !important;
          padding: 8px 32px 8px 12px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 6px !important;
          background-color: white !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
          background-position: right 8px center !important;
          background-repeat: no-repeat !important;
          background-size: 20px 20px !important;
          font-size: 14px !important;
          line-height: 1.5 !important;
          color: #374151 !important;
          cursor: pointer !important;
          margin: 12px 0 !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          position: relative !important;
          z-index: 100 !important;
          pointer-events: auto !important;
        }
        .qr-scanner-container select:focus {
          outline: none !important;
          border-color: #9ca3af !important;
          box-shadow: 0 0 0 2px rgba(156, 163, 175, 0.3) !important;
        }
        .qr-scanner-container select:hover {
          border-color: #9ca3af !important;
        }
        .qr-scanner-container select option {
          padding: 8px 12px !important;
          background-color: white !important;
          color: #374151 !important;
        }
        @media (max-width: 640px) {
          .qr-scanner-container select {
            height: 44px !important;
            font-size: 16px !important;
            padding: 10px 36px 10px 14px !important;
          }
        }
        
        /* Ensure the select container has proper positioning */
        .qr-scanner-container div[id$="__dashboard_section_csr"],
        .qr-scanner-container [id*="__camera_selection"] {
          position: relative !important;
          z-index: 50 !important;
        }
        
        /* Hide the info button and branding */
        .qr-scanner-container img[alt="Info icon"],
        .qr-scanner-container a[href*="scanapp.org"],
        .qr-scanner-container span[style*="position: absolute"][style*="right"],
        .qr-scanner-container [id$="__header_message"] a {
          display: none !important;
        }
        
        /* Video container responsive sizing */
        .qr-scanner-container video {
          max-width: 100% !important;
          height: auto !important;
        }
      `}</style>
      <div
        id={scannerId}
        className="w-full bg-gray-100 rounded-lg"
        style={{ minHeight: "450px" }}
      />
    </div>
  );
};
